import { CycleCountSessionItem } from '../models/cycle-count-session.model';

/**
 * #32233: the outcome of interpreting a RecordCount HTTP error. Present only when the error is a 409
 * optimistic-concurrency rejection (another counter recorded the item since the client loaded it).
 */
export interface CycleCountConflict {
  /** The server's current item to merge back into the grid, or null if the body was absent/malformed. */
  current: CycleCountSessionItem | null;
  /** User-facing "just counted by X, review before overwriting" message (falls back when no name). */
  message: string;
}

/**
 * #32233: classify a RecordCount error. Returns a {@link CycleCountConflict} when it is a 409 (stale save
 * rejected because someone else counted the item), or null for any other error so the caller keeps its own
 * generic/offline handling. Shared by the web and mobile counting screens (and the mobile offline-replay
 * reconcile) so the conflict message and current-item extraction are defined once. Takes `unknown` so callers
 * need not import HttpErrorResponse; it duck-types the `status`/`error` shape Angular's HttpClient produces.
 */
export function parseRecordCountConflict(
  err: unknown
): CycleCountConflict | null {
  if ((err as { status?: number })?.status !== 409) return null;
  return buildCycleCountConflict((err as { error?: unknown })?.error);
}

/**
 * #32233: build the conflict view-model from a current-item payload (a 409 body, online or from an offline
 * replay). Kept separate from {@link parseRecordCountConflict} so the offline queue's already-409-classified
 * callback can reuse the same message + current-item logic.
 */
export function buildCycleCountConflict(payload: unknown): CycleCountConflict {
  const item = payload as CycleCountSessionItem | null | undefined;
  const current = item?.cycleCountItemId ? item : null;
  const who = current?.countedByName?.trim();
  return {
    current,
    message: who
      ? `This item was just counted by ${who}, review before overwriting.`
      : 'This item was just counted by someone else, review before overwriting.',
  };
}
